import { useState } from "react";
import MarkdownContent from "../MarkdownContent";
import { copyToClipboard, resolveRef } from "../../utils/api";

function RequestBody({ endpoint, apiSpec }) {
  if (!endpoint.requestBody) return null;

  const content = endpoint.requestBody.content?.["application/json"];
  if (!content) return null;

  const schema = content.schema?.$ref ? resolveRef(apiSpec, content.schema.$ref) : content.schema;

  let exampleToShow = null;
  let exampleSummary = null;

  if (content.examples) {
    const [firstExampleKey] = Object.keys(content.examples);
    if (firstExampleKey) {
      const firstExample = content.examples[firstExampleKey];
      exampleToShow = firstExample.value;
      exampleSummary = firstExample.summary || firstExample.description;
    }
  } else if (content.example) {
    exampleToShow = content.example;
  }

  return (
    <div>
      <h3>Request Body</h3>
      {endpoint.requestBody.description && (
        <MarkdownContent content={endpoint.requestBody.description} />
      )}

      {endpoint.requestBody.required && (
        <p style={{ marginBottom: "12px" }}>
          <span className="param-required">Required</span>
        </p>
      )}

      {schema && (
        <div style={{ marginBottom: "16px" }}>
          <h4>Schema</h4>
          <p>
            <span className="param-type">
              {content.schema.$ref ? content.schema.$ref.split("/").pop() : "object"}
            </span>
          </p>
          {schema.description && <MarkdownContent content={schema.description} />}
        </div>
      )}

      {exampleToShow && (
        <div>
          <h4>{exampleSummary || "Example Request"}</h4>
          <div className="code-block">
            <div className="code-header">
              <span className="code-language">JSON</span>
              <button
                className="copy-button"
                onClick={() => copyToClipboard(JSON.stringify(exampleToShow, null, 2))}
              >
                Copy
              </button>
            </div>
            <pre>{JSON.stringify(exampleToShow, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function Parameters({ endpoint, apiSpec }) {
  if (!endpoint.parameters || endpoint.parameters.length === 0) return null;

  return (
    <div>
      <h3>Parameters</h3>
      <table className="params-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>In</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {endpoint.parameters.map((param, idx) => {
            const paramDef = param.$ref ? resolveRef(apiSpec, param.$ref) : param;
            return (
              <tr key={`${paramDef.name}-${idx}`}>
                <td>
                  <span className="param-name">{paramDef.name}</span>
                  {paramDef.required && <span className="param-required">Required</span>}
                </td>
                <td>
                  <span className="param-type">{paramDef.schema?.type || "string"}</span>
                </td>
                <td>{paramDef.in}</td>
                <td>
                  {paramDef.description && <MarkdownContent content={paramDef.description} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Responses({ endpoint, apiSpec }) {
  if (!endpoint.responses) return null;

  return (
    <div>
      <h3>Responses</h3>
      {Object.entries(endpoint.responses).map(([code, response]) => {
        const content = response.content?.["application/json"];
        const schema = content?.schema?.$ref ? resolveRef(apiSpec, content.schema.$ref) : content?.schema;

        let exampleToShow = null;
        let exampleSummary = null;

        if (content?.examples) {
          const [firstExampleKey] = Object.keys(content.examples);
          if (firstExampleKey) {
            const firstExample = content.examples[firstExampleKey];
            exampleToShow = firstExample.value;
            exampleSummary = firstExample.summary || firstExample.description;
          }
        } else if (content?.example) {
          exampleToShow = content.example;
        }

        return (
          <div key={code} style={{ marginBottom: "24px" }}>
            <h4>
              <span className={`badge ${code.startsWith("2") ? "badge-success" : "badge-info"}`}>
                {code}
              </span>{" "}
              {response.description && (
                <span style={{ fontWeight: "normal", fontSize: "14px" }}>
                  {response.description}
                </span>
              )}
            </h4>

            {schema && content?.schema?.$ref && (
              <p style={{ marginBottom: "12px" }}>
                <span className="param-type">{content.schema.$ref.split("/").pop()}</span>
              </p>
            )}

            {exampleToShow && (
              <div>
                {exampleSummary && (
                  <p style={{ fontSize: "14px", marginBottom: "8px", fontWeight: 600 }}>
                    {exampleSummary}
                  </p>
                )}
                <div className="code-block">
                  <div className="code-header">
                    <span className="code-language">Response Example</span>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(JSON.stringify(exampleToShow, null, 2))}
                    >
                      Copy
                    </button>
                  </div>
                  <pre>{JSON.stringify(exampleToShow, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function EndpointSection({ apiSpec, method, path }) {
  const endpoint = apiSpec.paths[path][method];
  const [testResponse] = useState(null);

  const getMethodClass = (m) => m.toLowerCase();

  return (
    <div>
      <div className="content-header">
        <h1>
          <span className={`method-badge ${getMethodClass(method)}`}>
            {method.toUpperCase()}
          </span>
          {path}
        </h1>
        <p className="subtitle">{endpoint.summary}</p>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Description</h2>
        <MarkdownContent content={endpoint.description} />

        <h3>Endpoint</h3>
        <div className="endpoint">
          <span className={`method-badge ${getMethodClass(method)}`}>
            {method.toUpperCase()}
          </span>
          <div className="endpoint-path">{path}</div>
        </div>

        <Parameters endpoint={endpoint} apiSpec={apiSpec} />
        <RequestBody endpoint={endpoint} apiSpec={apiSpec} />
        <Responses endpoint={endpoint} apiSpec={apiSpec} />
      </div>
    </div>
  );
}
